import React from 'react';
import { 
  PrinterIcon, 
  DocumentArrowDownIcon, 
  EnvelopeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { RefoulementResponse } from '@/services/refoulementService';

interface RefoulementTicketProps {
  refoulementData: RefoulementResponse;
  peseeData: {
    reference: string;
    grossWeight: number;
    tareWeight: number;
    netWeight: number;
    vehicle: string;
    driver: string;
    product: string;
    site: string;
    tenant: string;
    grossAt: string;
    tareAt: string;
    grossUser: string;
    tareUser: string;
  };
  onClose: () => void;
  onPrint: () => void;
  onExport: () => void;
  onEmail: () => void;
}

const RefoulementTicket: React.FC<RefoulementTicketProps> = ({
  refoulementData,
  peseeData,
  onClose,
  onPrint,
  onExport,
  onEmail
}) => {
  const formatWeight = (weight: number) => `${weight.toLocaleString()} kg`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-FR');

  const getCriticiteColor = (criticite: string) => {
    switch (criticite?.toLowerCase()) {
      case 'critique': return 'text-red-600 bg-red-100';
      case 'haute': return 'text-orange-600 bg-orange-100';
      case 'moyenne': return 'text-yellow-600 bg-yellow-100';
      case 'faible': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                <XMarkIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Ticket de Refoulement
                </h3>
                <p className="text-sm text-gray-600">
                  N° {refoulementData.numero_refoulement}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Ticket Content */}
          <div className="p-6">
            {/* En-tête du ticket */}
            <div className="text-center border-b-2 border-red-300 pb-4 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div className="text-left">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-red-600 rounded-full mr-2 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">REF</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Système de Pesée</h3>
                      <p className="text-xs text-gray-600">Refoulement de pesée</p>
                    </div>
                  </div>
                </div>
                
                <div className="text-right text-sm">
                  <div className="space-y-1">
                    <div><span className="text-red-600 font-medium">Date refoulement:</span> {formatDate(refoulementData.date_refoulement)}</div>
                    <div><span className="text-red-600 font-medium">Pesée:</span> {peseeData.reference}</div>
                    <div><span className="text-red-600 font-medium">Agent:</span> {refoulementData.agent_refoulement_nom || refoulementData.agent_refoulement_username}</div>
                  </div>
                </div>
              </div>
              
              <h1 className="text-3xl font-bold text-red-600 mb-2">TICKET DE REFOULEMENT</h1>
              <div className="text-lg font-semibold text-gray-700 mb-2">
                <span className="mr-4">PESEE</span>
                <span>REFOULEE</span>
              </div>
              
              <div className="text-2xl font-mono text-red-600 font-bold">
                {refoulementData.numero_refoulement}
              </div>
            </div>

            {/* Informations de la pesée refoulée */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Informations de la pesée</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Référence:</span>
                    <span className="text-red-600 font-medium">{peseeData.reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Véhicule:</span>
                    <span className="font-medium">{peseeData.vehicle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Chauffeur:</span>
                    <span className="font-medium">{peseeData.driver}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Produit:</span>
                    <span className="font-medium">{peseeData.product}</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Poids brut:</span>
                    <span className="text-red-600 font-medium">{formatWeight(peseeData.grossWeight)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Poids tare:</span>
                    <span className="font-medium">{formatWeight(peseeData.tareWeight)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Poids net:</span>
                    <span className="text-red-600 font-bold text-lg">{formatWeight(peseeData.netWeight)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Site:</span>
                    <span className="font-medium">{peseeData.site}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Motifs de refoulement */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Motifs de refoulement</h2>
              <div className="space-y-3">
                {refoulementData.motifs.map((motif, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{motif.libelle || motif.nom || `Motif ${index + 1}`}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCriticiteColor(motif.criticite)}`}>
                        {motif.criticite || 'Non défini'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{motif.description || 'Description non disponible'}</p>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Catégorie:</span> {motif.categorie || 'Non définie'}
                      {motif.code && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="font-medium">Code:</span> {motif.code}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Observations */}
            {refoulementData.observations && (
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Observations</h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{refoulementData.observations}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium text-gray-900 mb-4">Actions disponibles</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <button
                  onClick={onPrint}
                  className="flex flex-col items-center p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <PrinterIcon className="w-5 h-5 mb-1" />
                  <span className="text-xs">Imprimer</span>
                </button>

                <button
                  onClick={onExport}
                  className="flex flex-col items-center p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <DocumentArrowDownIcon className="w-5 h-5 mb-1" />
                  <span className="text-xs">Exporter PDF</span>
                </button>

                <button
                  onClick={onEmail}
                  className="flex flex-col items-center p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  <EnvelopeIcon className="w-5 h-5 mb-1" />
                  <span className="text-xs">Envoyer Email</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefoulementTicket;